import { useEffect, useRef, useState } from 'react';
import type { ReportDetail, ReportTone } from '@/utils/strategyReport';

interface GaugeBarProps {
  label: string;
  display: string;
  value: number;
  min: number;
  max: number;
  minLabel: string;
  maxLabel: string;
  detail?: ReportDetail;
  align?: 'left' | 'right';
}

const rowToneClass: Record<ReportTone, string> = {
  default: 'text-foreground-900',
  profit: 'text-profit-600',
  loss: 'text-loss-500',
};

// A single MetaTrader report gauge: hairline track, filled portion to the value,
// and a small value chip riding the fill — plus the min/max scale underneath.
// Gauges that carry a `detail` reveal the story behind the number: on hover for
// pointer devices, and on tap (expand/collapse) for touch devices.
function GaugeBar({
  label,
  display,
  value,
  min,
  max,
  minLabel,
  maxLabel,
  detail,
  align = 'left',
}: GaugeBarProps) {
  const ratio = max > min ? (value - min) / (max - min) : 0;
  // Keep the chip inside the track even at the very ends of the scale.
  const percent = Math.min(Math.max(ratio, 0), 1) * 100;
  const chipLeft = Math.min(Math.max(percent, 4), 96);

  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [canHover, setCanHover] = useState(true);

  // Decide once which interaction model applies to this device.
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      setCanHover(window.matchMedia('(hover: hover) and (pointer: fine)').matches);
    }
  }, []);

  // On touch devices, a tap outside an expanded gauge collapses it again.
  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (event: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [open]);

  const toggle = () => {
    if (detail && !canHover) setOpen((prev) => !prev);
  };

  const panelStateClass = canHover
    ? 'translate-y-1 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:translate-y-0 group-focus-within:opacity-100'
    : open
      ? 'translate-y-0 opacity-100'
      : 'translate-y-1 opacity-0';

  const iconClass = detail && !canHover && open ? 'ri-subtract-line' : 'ri-information-line';

  return (
    <div
      ref={rootRef}
      className={`group relative${detail ? ' outline-none' : ''}${
        detail ? (canHover ? ' cursor-help' : ' cursor-pointer') : ''
      }`}
      tabIndex={detail ? 0 : undefined}
      aria-label={detail ? `${label} — tap for detail` : undefined}
      aria-expanded={detail && !canHover ? open : undefined}
      onClick={toggle}
    >
      <div className="flex items-center gap-1.5">
        <p className="text-[12px] text-foreground-600 whitespace-nowrap">{label}</p>
        {detail ? (
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className={`${iconClass} text-[12px] text-foreground-400`} />
          </span>
        ) : null}
      </div>

      <div className="relative mt-3 h-6">
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-background-300" />
        <div
          className="absolute left-0 top-1/2 -translate-y-1/2 h-[3px] bg-foreground-800"
          style={{ width: `${percent}%` }}
        />
        <div
          className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 inline-flex h-6 min-w-[36px] items-center justify-center rounded-sm bg-foreground-800 px-1.5 font-data text-[11px] text-background-50"
          style={{ left: `${chipLeft}%` }}
        >
          {display}
        </div>
      </div>

      <div className="mt-1.5 flex justify-between font-data text-[10px] text-foreground-400">
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </div>

      {detail ? (
        <div
          className={`pointer-events-none absolute bottom-full z-30 mb-2 w-[268px] max-w-[82vw] transition duration-150 ${panelStateClass} ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          <div className="rounded-lg border border-background-300 bg-background-50 p-3.5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-foreground-500">
              {detail.title}
            </p>

            <div className="mt-2.5 flex flex-col gap-1.5">
              {detail.rows.map((row) => (
                <div key={row.label} className="flex items-baseline justify-between gap-3">
                  <span className="text-[11px] text-foreground-500">{row.label}</span>
                  <span
                    className={`font-data text-[11px] font-semibold whitespace-nowrap ${rowToneClass[row.tone]}`}
                  >
                    {row.value}
                  </span>
                </div>
              ))}
            </div>

            <p className="mt-2.5 border-t border-background-200 pt-2.5 text-[10px] leading-relaxed text-foreground-500">
              {detail.note}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default GaugeBar;